import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
  alpha,
  Box,
  Divider,
  Drawer,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';

import DrawerHeader from 'components/DrawerHeader';
import TabPanelHistorico from 'components/FilaEspera/components/TabPanelHistorico';
import HistoryMessagesSearch from 'components/HistoryMessagesSearch';
import TabPanel from 'components/TabPanel';
import SearchQueryContext from 'contexts/SearchQueryContext';
import { useAppDispatch, useAppSelector } from 'store';
import { AtendimentoInterface } from 'store/reducers/atendimentos';
import {
  fetchCanaisSelect,
  SelectChannelInterface,
} from 'store/reducers/canais';
import {
  ContatoInterfaceV2,
  contatosSelector,
  fetchContatoAtendimentoV2,
  resetContatoAtendimentoV2,
} from 'store/reducers/contatos';
import {
  navegarParaMensagem,
  resetIdMensagemPesquisada,
  setIdMensagemPesquisada,
} from 'store/reducers/mensagens';
import { fetchTags } from 'store/reducers/tags';

import ChannelSelectField from './ChannelSelectField';
import ContactFunnelTabPanel from './ContactFunnelTabPanel';
import DrawerDetalhesContatoSkeleton from './DrawerDetalhesContatoSkeleton';
import TabPanelArquivos from './TabPanelArquivos';
import TabPanelAtendimento from './TabPanelAtendimento';
import TabPanelAtividadesContato from './TabPanelAtividadesContato';
import TabPanelInformacoesContato from './TabPanelInformacoesContato';

const useStyles = makeStyles(({ appDrawer, spacing, palette }) => ({
  drawer: {
    width: appDrawer.minWidth,
  },
  drawerPaper: {
    width: appDrawer.minWidth,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  tab: {
    borderTop: `1px solid ${palette.divider}`,
    '& .MuiTab-root': {
      minWidth: 'auto',
    },
  },
  tabPanel: {
    flex: 1,
    padding: spacing(2),
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  tabPanelHistory: {
    flex: 1,
    padding: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  stickySelect: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: palette.background.paper,
    borderBottom: `1px solid ${alpha(palette.divider, 0.1)}`,
    padding: spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
  },

  backgroundAvatar: {
    position: 'relative',
    width: spacing(12.5),
    height: spacing(12.5),
    backgroundColor: alpha(palette.primary.main, 0.2),
    borderRadius: spacing(1),
    borderTopRightRadius: spacing(2),
    borderBottomRightRadius: spacing(2),
    marginRight: spacing(5),
  },
  avatarBadge: {
    position: 'absolute',
    top: 0,
    right: -35,
  },
  avatarLarge: {
    width: spacing(12.5),
    height: spacing(12.5),
    border: `2px solid ${alpha(palette.primary.main, 0.3)}`,
  },
}));

type TabKey =
  | 'contacts'
  | 'activities'
  | 'files'
  | 'attendance'
  | 'history'
  | 'funnel';

interface DrawerDetalhesContatoProps {
  open: boolean;
  contatoId: string;
  atendimento?: AtendimentoInterface;
  tabIndex?: TabKey;
  editMode?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function DrawerDetalhesContato({
  open,
  atendimento,
  editMode = false,
  tabIndex = 'contacts',
  contatoId,
  onClose,
  onSuccess,
}: DrawerDetalhesContatoProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const {
    contatoAtendimentoV2: contato = {} as ContatoInterfaceV2,
    loadingContatoAtendimentoV2: loading,
  } = useAppSelector(contatosSelector);

  const [tabValue, setTabValue] = useState<TabKey>('contacts');
  const [enableEditMode, setEnableEditMode] = useState(editMode);
  const [selectedChannelCode, setSelectedChannelCode] = useState<string>('');
  const [searchCursor, setSearchCursor] = useState<string | undefined>();
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  const handleTabChange = (_: ChangeEvent<unknown>, newValue: TabKey) => {
    setTabValue(newValue);
    setEnableEditMode(false);
    setSelectedChannelCode('');
    setSearchCursor(undefined);
    setLocalSearchQuery('');
  };

  const handleCanalChange = useCallback((canal: SelectChannelInterface) => {
    setSelectedChannelCode(canal.code);
    setSearchCursor(undefined);
    setLocalSearchQuery('');
  }, []);

  const handleNavigateSearch = useCallback(
    async (messageId: string) => {
      try {
        const data = await dispatch(navegarParaMensagem(messageId)).unwrap();
        setSearchCursor(data.data.afterCursor);
        dispatch(setIdMensagemPesquisada(messageId));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error navigating message:', error);
      }
    },
    [dispatch],
  );

  const handleClearSearch = useCallback(() => {
    setSearchCursor(undefined);
    dispatch(resetIdMensagemPesquisada());
    setLocalSearchQuery('');
  }, [dispatch]);

  useEffect(() => {
    if (open) {
      setTabValue(tabIndex);

      if (contatoId) {
        dispatch(fetchContatoAtendimentoV2(contatoId));
        dispatch(fetchCanaisSelect());
        dispatch(fetchTags());
      }
    }
  }, [open, contatoId, dispatch, tabIndex]);

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        value: 'contacts' as TabKey,
        label: 'Contatos',
        render: () =>
          loading ? (
            <DrawerDetalhesContatoSkeleton />
          ) : (
            <TabPanelInformacoesContato
              contato={contato}
              editMode={enableEditMode}
              onEnableEditMode={setEnableEditMode}
              isAtendimento={Boolean(atendimento)}
              onSuccess={onSuccess}
            />
          ),
      },
      {
        value: 'activities' as TabKey,
        label: 'Atividades',
        render: () => (
          <TabPanelAtividadesContato
            contato={contato ?? ({} as ContatoInterfaceV2)}
          />
        ),
      },
      {
        value: 'files' as TabKey,
        label: 'Arquivos',
        render: () => (
          <TabPanelArquivos contatoId={contatoId} atendimento={atendimento} />
        ),
      },
    ];

    if (atendimento) {
      baseTabs.push({
        value: 'attendance',
        label: 'Atendimento',
        render: () => (
          <TabPanelAtendimento
            contatoId={contatoId}
            atendimentoId={atendimento.id}
          />
        ),
      });
    }

    if (!atendimento) {
      baseTabs.push({
        value: 'history',
        label: 'Histórico',
        render: () => (
          <Box
            bgcolor="background.default"
            p={2}
            height="100%"
            display="flex"
            flexDirection="column"
          >
            {selectedChannelCode ? (
              <Box flex={1} overflow="auto">
                <TabPanelHistorico
                  contatoId={contatoId}
                  channelCode={selectedChannelCode}
                  initialCursor={searchCursor}
                  searchQuery={localSearchQuery}
                />
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flex={1}
              >
                <Typography variant="body2" color="textSecondary">
                  Selecione um canal para visualizar o histórico
                </Typography>
              </Box>
            )}
          </Box>
        ),
      });
    }

    baseTabs.push({
      value: 'funnel',
      label: 'Funil',
      render: () => <ContactFunnelTabPanel contactId={contatoId} />,
    });

    return baseTabs;
  }, [
    atendimento,
    loading,
    contato,
    enableEditMode,
    onSuccess,
    contatoId,
    selectedChannelCode,
    searchCursor,
    localSearchQuery,
  ]);

  let title = 'Detalhes do contato';

  if (enableEditMode) {
    title = 'Editar contato';
  } else if (atendimento) {
    title = 'Detalhes';
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      BackdropProps={{
        onExited: () => {
          dispatch(resetContatoAtendimentoV2());
          setEnableEditMode(editMode);
        },
      }}
    >
      <DrawerHeader
        title={
          <Typography variant="body1" style={{ fontWeight: 500 }}>
            {title}
          </Typography>
        }
        onClose={onClose}
        whithoutDivider
      />

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        centered
        className={classes.tab}
      >
        {tabs.map(tab => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>

      {tabValue === 'history' && (
        <Box className={classes.stickySelect}>
          <Box flex={1}>
            <ChannelSelectField
              value={selectedChannelCode}
              onFilter={handleCanalChange}
              onReset={() => setSelectedChannelCode('')}
              style={{ width: '100%' }}
            />
          </Box>

          {selectedChannelCode && (
            <Box flex={1}>
              <HistoryMessagesSearch
                contactId={contatoId}
                channelCode={selectedChannelCode}
                onNavigate={handleNavigateSearch}
                onClear={handleClearSearch}
                onSearchChange={setLocalSearchQuery}
              />
            </Box>
          )}
        </Box>
      )}

      <Divider />

      <SearchQueryContext.Provider value={localSearchQuery}>
        {tabs.map(tab => (
          <TabPanel
            key={tab.value}
            value={tabValue}
            index={tab.value}
            className={
              tab.value === 'history'
                ? classes.tabPanelHistory
                : classes.tabPanel
            }
          >
            {tab.render()}
          </TabPanel>
        ))}
      </SearchQueryContext.Provider>
    </Drawer>
  );
}
